import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    FieldGroup,
    Form,
    Heading,
    Label,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';

import { apiClient, useSiteContext } from '../../../../../state';
import { McpServerHeadersField } from './McpServerHeadersField';

export type McpServerFormInputs = {
    name: string;
    url: string;
    headers: { key: string; value: string }[];
};

export function AddMcpServerDialog() {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>New MCP server</Heading>
            </DialogHeader>
            <AddMcpServerForm />
        </Dialog>
    );
}

function AddMcpServerForm() {
    const { organizationId, site } = useSiteContext();
    const [isPending, startDialogAction] = useDialogAction();

    const form = useForm<McpServerFormInputs>({
        defaultValues: { name: '', url: '', headers: [{ key: '', value: '' }] },
    });

    const onSubmit: SubmitHandler<McpServerFormInputs> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const headers: Record<string, string> = {};
                data.headers.forEach((h) => {
                    if (h.key) headers[h.key] = h.value;
                });
                await apiClient.siteMcpServers.createSiteMcpServer(organizationId, site.id, {
                    name: data.name,
                    url: data.url as api.URL,
                    headers,
                });
                analytics.logEvent(
                    analytics.createSiteEvent(
                        { id: site.id, organization: organizationId },
                        {
                            type: 'site_create_mcp_server',
                        }
                    )
                );
                toast.success('MCP server added');
                await close();
            }, 'Error while adding MCP server');
        });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody className="flex flex-col gap-4">
                <FieldGroup>
                    <TextField name="name" required>
                        <Label>Name</Label>
                        <TextInput autoFocus disabled={isPending} />
                        <Description>
                            The name of the MCP server. This will not be visible to your users.
                        </Description>
                        <FieldError />
                    </TextField>
                </FieldGroup>
                <TextField name="url" required>
                    <Label>MCP Server URL</Label>
                    <TextInput placeholder="https://" disabled={isPending} />
                    <FieldError />
                </TextField>
                <McpServerHeadersField name="headers" disabled={isPending} />
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Create" />
            </DialogFooter>
        </Form>
    );
}
